/**
 * Rows 2–9 of the locked D1 grid (spec §3.2). Facts boxes draw a view state;
 * app boxes draw /looker's AppTileBody unchanged (R11). Below `md` every row
 * is one column, in box order. Box 11 spans the row when box 10 is hidden.
 * Box 11 and box 13 rows open the Channel Deep-dive with `linkSearch`, the
 * shared filters (D2 R19) in the currency the page shows, plus scope=all in
 * the master's all-workspaces view.
 *
 * When the channels in view spent nothing (`view.empty`, rule 04.3), or the one
 * ticked channel is known not to be connected, spend or not (rules-04 H3), D2's card
 * replaces box 2 and boxes 3–5, 9 and 11–13 are not drawn. Boxes 6–8 and 10 are
 * app boxes that do not follow the channel filter, so they stay (box 10 spans
 * its row, box 11 being gone). The grid builds the app boxes once and returns
 * either that card layout or the facts layout, UpperBoxes and LowerBoxes around them.
 *
 * @module components/overview/OverviewGrid
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelEmptyState } from '@/components/channels/ChannelEmptyState';
import { AppTileBody } from '@/components/looker/app/AppTileBody';
import { isHiddenWhenEmpty, type AppTileState } from '@/hooks/useLookerAppTiles';
import type { CoverageState } from '@/hooks/useLookerCoverage';
import { cardEmptyOf, soleChannelLabel } from '@/services/channels/channelFormat';
import type { AppWidget } from '@/services/looker/lookerAppTiles';
import { rangeDays } from '@/services/looker/lookerDateRange';
import type { ResultMappingInfo } from '@/services/targets/resultMetric';
import type { OverviewWindows } from '@/services/overview/overviewTypes';
import type { OverviewView } from '@/services/overview/overviewView';
import { OverviewBox, OverviewCard } from './OverviewBox';
import { ScorecardRow } from './ScorecardRow';
import { ResultCards } from './ResultCards';
import { SpendClicksChart } from './SpendClicksChart';
import { ChannelDonut } from './ChannelDonut';
import { ObjectiveTable } from './ObjectiveTable';
import { AccountTable, ChannelTable, TopCampaignsTable } from './MetricTables';

interface Props {
  view: OverviewView;
  windows: OverviewWindows;
  /** null = still loading (boxes 3 and 9 are loading too, so it is never read then). */
  mapping: ResultMappingInfo | null;
  app: Record<AppWidget, AppTileState>;
  coverage: CoverageState;
  channelFiltered: boolean;
  /** The shared filters as a query (linkSearchOf): the currency the page shows, and scope=all while it shows all workspaces. Carried by the box 11 and box 13 links. */
  linkSearch: string;
}

interface FactsProps extends Pick<Props, 'view' | 'windows' | 'mapping' | 'linkSearch'> {
  currency: string | null;
}

interface LowerProps extends FactsProps {
  /** Box 10, drawn by the grid; null = hidden, and box 11 spans its row. */
  outcomes: React.ReactElement | null;
}

/** Boxes 2–5: the key figures, the Result card when the view has one, and the trend beside the channel share. */
const UpperBoxes: React.FC<FactsProps> = ({ view, windows, mapping, currency }) => {
  const { t } = useTranslation();
  return (
    <>
      <OverviewBox id="2" title={t('overview.box.scorecards')} state={view.scorecards}>
        {(cards) => <ScorecardRow cards={cards} currency={currency} />}
      </OverviewBox>

      {view.result && (
        <OverviewBox id="3" title={t('overview.box.result')} state={view.result}>
          {(card) => (mapping ? <ResultCards card={card} windows={windows} mapping={mapping} currency={currency} /> : null)}
        </OverviewBox>
      )}

      <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
        <OverviewBox id="4" title={t('overview.box.trend')} state={view.trend}>
          {(trend) => <SpendClicksChart trend={trend} currency={currency} />}
        </OverviewBox>
        <OverviewBox id="5" title={t('overview.box.share')} state={view.share}>
          {(share) => <ChannelDonut share={share} currency={currency} />}
        </OverviewBox>
      </div>
    </>
  );
};

/** Boxes 9–13: the objectives, box 10 beside box 11, the accounts and the top campaigns. */
const LowerBoxes: React.FC<LowerProps> = ({ view, windows, mapping, currency, linkSearch, outcomes }) => {
  const { t } = useTranslation();
  return (
    <>
      <OverviewBox id="9" title={t('overview.box.objectives')} state={view.objectives}>
        {(table) => (mapping ? <ObjectiveTable table={table} windows={windows} mapping={mapping} currency={currency} /> : null)}
      </OverviewBox>

      <div className="grid gap-4 md:grid-cols-2">
        {outcomes}
        <OverviewBox id="11" title={t('overview.box.channels')} state={view.channels} className={outcomes ? '' : 'md:col-span-2'}>
          {(rows) => <ChannelTable rows={rows} currency={currency} linkSearch={linkSearch} />}
        </OverviewBox>
      </div>

      <OverviewBox id="12" title={t('overview.box.accounts')} state={view.accounts}>
        {(rows) => <AccountTable rows={rows} />}
      </OverviewBox>

      <OverviewBox id="13" title={t('overview.box.campaigns')} state={view.campaigns}>
        {(rows) => <TopCampaignsTable rows={rows} currency={currency} cutApplied={windows.cutApplied} linkSearch={linkSearch} mapping={mapping} />}
      </OverviewBox>
    </>
  );
};

export const OverviewGrid: React.FC<Props> = ({ view, windows, mapping, app, coverage, channelFiltered, linkSearch }) => {
  const { t } = useTranslation();
  const { empty } = view;
  const appBox = (
    id: string, title: string, state: AppTileState, opts: { withCoverage?: boolean; className?: string } = {},
  ): React.ReactElement => (
    <OverviewCard id={id} title={title} className={opts.className}>
      <AppTileBody state={state} channelFiltered={channelFiltered} {...(opts.withCoverage ? { coverage } : {})} />
    </OverviewCard>
  );
  const pacing = (
    <div className="grid gap-4 md:grid-cols-3">
      {appBox('6', t('overview.box.pacing'), app.budgetPacing)}
      {appBox('7', t('overview.box.attention'), app.attention)}
      {appBox('8', t('overview.box.quality'), app.dataQuality, { withCoverage: true })}
    </div>
  );
  const outcomes = isHiddenWhenEmpty(app.outcomes)
    ? null
    : appBox('10', t('overview.box.outcomes'), app.outcomes, { className: empty ? 'md:col-span-2' : '' });

  if (empty) {
    return (
      <div className="space-y-4">
        <ChannelEmptyState
          empty={cardEmptyOf(empty)} channelLabel={soleChannelLabel(empty.channels)}
          windowDays={rangeDays(windows.current)}
        />
        {pacing}
        {outcomes && <div className="grid gap-4 md:grid-cols-2">{outcomes}</div>}
      </div>
    );
  }
  const facts = { view, windows, mapping, currency: view.money?.currency ?? null, linkSearch };
  return (
    <div className="space-y-4">
      <UpperBoxes {...facts} />
      {pacing}
      <LowerBoxes {...facts} outcomes={outcomes} />
    </div>
  );
};
